import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid } from '@mui/material';
import classNames from 'classnames';
import { SGSCheckboxCtr, SGSForm, SGSFormDate, SGSFormLayout, SGSRadioCtr } from 'components';
import { useCustomStyles } from 'core/mui/customStyles';
import { useArretTravailFormContext } from 'features/arretTravail/contexts/ArretTravailFormProvider';
import useValidationEffects from 'features/arretTravail/hooks/useValidationEffects';
import { FC } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import styles from '../../../css/ArretTravail.module.scss';

type ValidationSchema = {
    subrogation: {
        hasSubrogation: boolean;
        startDate: Date;
        endDate: Date;
        maintienCod: string;
    };
};

type Props = {
    store: any;
    onSubmit: SubmitHandler<ValidationSchema>;
    actions?: JSX.Element;
};

const FormSubrogation: FC<Props> = ({ store, onSubmit, actions }) => {
    const customStyles = useCustomStyles();
    const { activeStep, setStepFailed } = useArretTravailFormContext();

    // Validation schema
    const validationSchema: Yup.SchemaOf<ValidationSchema> = Yup.object().shape({
        subrogation: Yup.object().shape({
            hasSubrogation: Yup.boolean().nullable(),
            startDate: Yup.date()
                .nullable()
                .when('hasSubrogation', {
                    is: true,
                    then: (schema) => schema.required('Veuillez saisir la date de début de subrogation.').typeError('La date de début de subrogation est incorrecte.'),
                    otherwise: (schema) => schema.nullable(), // Allow null if hasSubrogation is false
                }),
            endDate: Yup.date()
                .nullable()
                .when('hasSubrogation', {
                    is: true,
                    then: (schema) =>
                        schema
                            .required('Veuillez saisir la date de fin de subrogation.')
                            .typeError('La date de fin de subrogation est incorrecte.')
                            .min(Yup.ref('startDate'), 'La date de fin doit être après la date de début.'),
                    otherwise: (schema) => schema.nullable(), // Allow null if hasSubrogation is false
                }),
            maintienCod:
                store.cerfa === 'S6202'
                    ? Yup.string()
                          .nullable()
                          .when('hasSubrogation', {
                              is: true,
                              then: (schema) => schema.required('Veuillez sélectionner si le salaire sera maintenu.'),
                              otherwise: (schema) => schema.nullable(), // Allow null if hasSubrogation is false
                          })
                    : Yup.string().nullable(),
        }),
    });

    // Initialize form methods
    const methods = useForm<ValidationSchema>({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            subrogation: {
                hasSubrogation: store.subrogation?.hasSubrogation ?? false,
                startDate: store.subrogation?.startDate ?? null,
                endDate: store.subrogation?.endDate ?? null,
                maintienCod: store.subrogation?.maintienCod ?? null,
            },
        },
    });

    const {
        handleSubmit,
        formState: { errors },
        control,
        watch,
    } = methods;

    const hasSubrogation = watch('subrogation.hasSubrogation');

    console.log(errors);

    // Handle validation effects
    useValidationEffects({ errors, activeStep, setStepFailed, control });

    const subrogationOptions = [
        { value: 'NON', label: 'Non' },
        { value: 'INT', label: 'Intégralement' },
        { value: 'PAR', label: 'Partiellement' },
    ];

    console.log(store);

    return (
        <FormProvider {...methods}>
            <SGSFormLayout onSubmit={handleSubmit(onSubmit)} actions={actions}>
                <Grid container justifyContent="center">
                    <Grid item xs={12} md={6}>
                        <Box className={styles.title}>Subrogation</Box>

                        <SGSForm.Row>
                            <SGSForm.Col>
                                <SGSCheckboxCtr name="subrogation.hasSubrogation" label="Demande de subrogation en cas de maintient de salaire" />
                            </SGSForm.Col>
                        </SGSForm.Row>

                        {hasSubrogation && (
                            <>
                                <SGSForm.Row>
                                    <Box className={classNames(customStyles.txtBlueLight, 'mb-2')}>Période pendant laquelle l'employeur demande la subrogation</Box>
                                    <SGSForm.Col md={6}>
                                        <SGSFormDate inputVariant="outlined" name="subrogation.startDate" label="Du" />
                                    </SGSForm.Col>
                                    <SGSForm.Col md={6}>
                                        <SGSFormDate inputVariant="outlined" name="subrogation.endDate" label="Au" />
                                    </SGSForm.Col>
                                </SGSForm.Row>

                                {store.cerfa === 'S6202' && (
                                    <SGSForm.Row>
                                        <SGSForm.Col>
                                            <SGSRadioCtr row options={subrogationOptions} name="subrogation.maintienCod" label="Le salaire sera maintenu " />
                                        </SGSForm.Col>
                                    </SGSForm.Row>
                                )}

                                {/* Additional instructional text */}
                                <Box className={'m-3'}>
                                    <Box className={classNames(customStyles.txtBlueLight)}>Les dates de début et de fin de la période pendant laquelle vous demandez la subrogation</Box>
                                    <Box className={classNames('mt-2', customStyles.txtBlueLight)}>- date de début : précisez la date de début de l'arrêt de travail ou du congé</Box>
                                    <Box className={classNames(customStyles.txtBlueLight)}>
                                        - date de fin : précisez la date de fin de la durée maximale de maintien du salaire et de la subrogation, telle qu'elle est définie par votre convention
                                        collective ou votre accord de branche.
                                    </Box>
                                    <Box className={classNames('mt-2', customStyles.txtBlueLight)}>
                                        Important : N'indiquez pas la date de fin de l'arrêt de travail ou du congé, mais bien la date de fin de la période pendant laquelle peut s'appliquer le
                                        maintien du salaire et la subrogation, même si la durée de l'arrêt de travail ou du congé ne couvre pas toute cette période. Pour une 1ère demande de
                                        subrogation ou pour un changement de domiciliation bancaire, le RIB (Relevé d'Identité Bancaire) doit être joint à la déclaration.
                                    </Box>
                                </Box>
                            </>
                        )}
                    </Grid>
                </Grid>
            </SGSFormLayout>
        </FormProvider>
    );
};

export default FormSubrogation;
